import type { Language } from '../src/types';

/**
 * Utility functions for managing language-based URLs
 */
export class LanguageUtils {
  private static readonly VALID_LANGUAGES: Language[] = ['si', 'en'];
  private static readonly DEFAULT_LANGUAGE: Language = 'en';

  /**
   * Check if a language code is valid
   */
  static isValidLanguage(lang: string): lang is Language {
    return this.VALID_LANGUAGES.includes(lang as Language);
  }

  /**
   * Get the default language
   */
  static getDefaultLanguage(): Language {
    return this.DEFAULT_LANGUAGE;
  }

  /**
   * Extract language from URL path
   */
  static extractLanguageFromPath(path: string): { language: Language; pathWithoutLang: string } {
    const match = path.match(/^\/([a-z]{2})(\/.*)?$/);
    
    if (match) {
      const [, langCode, remainingPath] = match;
      const language = this.isValidLanguage(langCode) ? langCode : this.DEFAULT_LANGUAGE;
      const pathWithoutLang = remainingPath || '';
      return { language, pathWithoutLang };
    }

    return { language: this.DEFAULT_LANGUAGE, pathWithoutLang: path };
  }

  /**
   * Build a URL with language prefix
   */
  static buildLanguageUrl(language: Language, path: string = ''): string {
    // Ensure path starts with / if not empty
    const normalizedPath = path && !path.startsWith('/') ? `/${path}` : path;
    return `/${language}${normalizedPath}`;
  }

  /**
   * Switch language in current URL
   */
  static switchLanguageInUrl(currentUrl: string, newLanguage: Language): string {
    const { pathWithoutLang } = this.extractLanguageFromPath(currentUrl);
    return this.buildLanguageUrl(newLanguage, pathWithoutLang);
  }

  /**
   * Get localized path for navigation
   * This function can be used with React Router's navigation functions
   */
  static getLocalizedPath(language: Language, path: string): string {
    // Remove leading slash if exists to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return this.buildLanguageUrl(language, cleanPath);
  }

  /**
   * Get saved language from localStorage with fallback
   */
  static getSavedLanguage(): Language {
    if (typeof window === 'undefined') {
      return this.DEFAULT_LANGUAGE;
    }

    const savedLanguage = localStorage.getItem('samadhi-language');
    return savedLanguage && this.isValidLanguage(savedLanguage) 
      ? savedLanguage 
      : this.DEFAULT_LANGUAGE;
  }

  /**
   * Save language to localStorage
   */
  static saveLanguage(language: Language): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('samadhi-language', language);
    }
  }

  /**
   * Check if URL needs language prefix redirect
   */
  static needsLanguageRedirect(path: string): { needsRedirect: boolean; redirectTo?: string } {
    const { pathWithoutLang } = this.extractLanguageFromPath(path);
    
    // If path is just "/" or doesn't start with a valid language code
    if (path === '/' || path === pathWithoutLang) {
      const savedLanguage = this.getSavedLanguage();
      return {
        needsRedirect: true,
        redirectTo: this.buildLanguageUrl(savedLanguage, pathWithoutLang)
      };
    }

    return { needsRedirect: false };
  }
}